import { useState, useCallback, useMemo, useEffect } from 'react'
import { format, getDate, getMonth, getYear } from 'date-fns'
import { DateRange } from '@mui/x-date-pickers-pro'
import debounce from 'lodash.debounce'
import { useAppDispatch } from '../../../redux/store'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import { manualRequestsService } from '../../../services/myActionsService'
import { getOrdinal, isValidPlannerDate } from '../../../utils/date-utils'
import { INPUT_CHANGE_API_DEBOUNCE_DELAY } from '../../../utils/constants'
import { AbsenceTypesEnum } from '../../Absence/types'
import { EmployeeOSPBalance, EmployeeBalanceTransaction } from '../../../types/osp-transactions'
import { AbsenceOverviewData } from '../AbsenceOverviewCard/types'
import { AbsenceOccurrence } from '../../../types/absence'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const CSP100_CODE = 'CSP100'
const CSP50_CODE = 'CSP50'

const formatDateDisplay = (startDate: Date, endDate: Date): { start: string; end: string } => {
  const startDay = getOrdinal(getDate(startDate))
  const endDay = getOrdinal(getDate(endDate))
  const endMonth = MONTHS[getMonth(endDate)]
  const endYear = getYear(endDate)

  const sameMonth = getMonth(startDate) === getMonth(endDate)
  const sameYear = getYear(startDate) === getYear(endDate)

  if (sameMonth && sameYear) {
    return { start: startDay, end: `${endDay} ${endMonth} ${endYear}` }
  }

  const startMonth = MONTHS[getMonth(startDate)]
  const startYear = getYear(startDate)
  return { start: `${startDay} ${startMonth} ${startYear}`, end: `${endDay} ${endMonth} ${endYear}` }
}

export interface UseAbsenceOverviewParams {
  employeeId?: number
  dateRange: DateRange<Date> | [Date | null, Date | null]
  selectedDays?: { day: Date | string; hours: number; checked: boolean }[] | null
  absenceCategoryId?: number
  extendAbsenceData?: AbsenceOccurrence
  enabled?: boolean
}

export interface UseAbsenceOverviewReturn {
  absenceOverviewData: AbsenceOverviewData | null
  isLoading: boolean
  ospBalances: EmployeeOSPBalance[]
}

export const useAbsenceOverview = ({
  employeeId,
  dateRange,
  selectedDays,
  absenceCategoryId,
  extendAbsenceData,
  enabled = true,
}: UseAbsenceOverviewParams): UseAbsenceOverviewReturn => {
  const dispatch = useAppDispatch()

  const [balances, setBalances] = useState<EmployeeOSPBalance[]>([])
  const [transactions, setTransactions] = useState<EmployeeBalanceTransaction[]>([])
  const [hasOsp50, setHasOsp50] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const isExtending = Boolean(extendAbsenceData)

  const apiStartDate = useMemo(() => {
    if (isExtending && extendAbsenceData?.startDate) {
      return new Date(extendAbsenceData.startDate)
    }
    return dateRange[0]
  }, [isExtending, extendAbsenceData?.startDate, dateRange[0]])

  const fetchBalances = useCallback(async () => {
    if (!employeeId || !isValidPlannerDate(apiStartDate) || !isValidPlannerDate(dateRange[1])) return

    setIsPending(false)
    setIsLoading(true)
    try {
      const response = await manualRequestsService.getEmployeeAbsenceTransactionsAndOSPBalance(
        employeeId,
        apiStartDate,
        dateRange[1]
      )
      setBalances(response.currentOSPBalances ?? [])
      setTransactions(response.employeeTransactions ?? [])
      setHasOsp50(response.hasOsp50 ?? false)
    } catch {
      dispatch(showErrorMessage('Failed to load absence overview data'))
      setBalances([])
      setTransactions([])
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, employeeId, apiStartDate, dateRange[1]])

  const debouncedFetch = useMemo(
    () => debounce(fetchBalances, INPUT_CHANGE_API_DEBOUNCE_DELAY),
    [fetchBalances]
  )

  useEffect(() => {
    if (!enabled) return
    if (absenceCategoryId !== AbsenceTypesEnum.Sickness) return

    if (employeeId && isValidPlannerDate(apiStartDate) && isValidPlannerDate(dateRange[1])) {
      setIsPending(true)
      debouncedFetch()
    }

    return () => {
      debouncedFetch.cancel()
      setIsPending(false)
    }
  }, [enabled, employeeId, apiStartDate, dateRange[1], absenceCategoryId, debouncedFetch])

  const originalAbsenceHours = useMemo(() => {
    if (!isExtending || !extendAbsenceData?.absenceDays) return 0
    return extendAbsenceData.absenceDays.reduce((total, day) => total + (day.hoursLost ?? 0), 0)
  }, [isExtending, extendAbsenceData?.absenceDays])

  const extensionHours = useMemo(() => {
    if (!selectedDays || selectedDays.length === 0) return 0
    return selectedDays.reduce((total, day) => total + (day.checked ? day.hours : 0), 0)
  }, [selectedDays])

  const totalRequestedHours = isExtending
    ? originalAbsenceHours + extensionHours
    : extensionHours

  const absenceOverviewData = useMemo((): AbsenceOverviewData | null => {
    if (!enabled) return null

    const displayStartDate = isExtending && extendAbsenceData?.startDate
      ? new Date(extendAbsenceData.startDate)
      : dateRange[0]

    const displayEndDate = dateRange[1]

    if (!isValidPlannerDate(displayStartDate) || !isValidPlannerDate(displayEndDate)) return null
    if (balances.length === 0) return null

    const { start, end } = formatDateDisplay(displayStartDate, displayEndDate)
    const hasSplitPots = hasOsp50

    const hundredPercentBalance = balances.find(balance => balance.balanceXRefCode === CSP100_CODE)
    const fiftyPercentBalance = hasSplitPots
      ? balances.find(balance => balance.balanceXRefCode === CSP50_CODE)
      : undefined

    const today = new Date()
    const todayStart = new Date(today)
    todayStart.setHours(0, 0, 0, 0)
    const absenceStartTime = new Date(displayStartDate)
    absenceStartTime.setHours(0, 0, 0, 0)
    const absenceEndTime = new Date(displayEndDate)
    absenceEndTime.setHours(23, 59, 59, 999)
    const isFutureAbsence = absenceStartTime.getTime() > todayStart.getTime()

    // Allowance includes today's balance plus every positive re-credit scheduled
    // between today and the end of the absence period. Re-credits landing inside
    // the period itself were previously excluded, which under-stated the paid
    // pot for future-dated absences whose OSP arrives during the period.
    const projectForward = (currentHours: number, code: string): number => {
      if (!isFutureAbsence) return currentHours
      const interimSum = transactions
        .filter(transaction => transaction.balanceXRefCode === code)
        .filter(transaction => transaction.amount > 0)
        .filter(transaction => {
          const txDate = new Date(transaction.transactionDate)
          return txDate >= todayStart && txDate <= absenceEndTime
        })
        .reduce((sum, transaction) => sum + transaction.amount, 0)
      return Math.max(0, currentHours + interimSum)
    }

    const hundredPercentAllowance = projectForward(hundredPercentBalance?.hours ?? 0, CSP100_CODE)
    const fiftyPercentAllowance = hasSplitPots
      ? projectForward(fiftyPercentBalance?.hours ?? 0, CSP50_CODE)
      : 0
    const totalAllowance = hundredPercentAllowance + fiftyPercentAllowance

    const hundredPercentUsed = Math.min(totalRequestedHours, hundredPercentAllowance)
    const remainingAfterHundred = Math.max(totalRequestedHours - hundredPercentAllowance, 0)

    const fiftyPercentUsed = hasSplitPots
      ? Math.min(remainingAfterHundred, fiftyPercentAllowance)
      : 0

    const unpaidHours = Math.max(totalRequestedHours - totalAllowance, 0)
    const exceededHours = unpaidHours

    return {
      startDate: start,
      endDate: end,
      totalAbsenceHours: totalRequestedHours,
      hasSplitPots,
      paidHours: hundredPercentUsed,
      fiftyPercentUsed: hasSplitPots ? fiftyPercentUsed : undefined,
      unpaidHours,
      exceededHours,
    }
  }, [enabled, balances, transactions, hasOsp50, dateRange[0], dateRange[1], totalRequestedHours, isExtending, extendAbsenceData?.startDate])

  return {
    absenceOverviewData,
    isLoading: isLoading || isPending,
    ospBalances: balances,
  }
}
